/**
 * Page dots for group-cells scroll-carousel instances.
 *
 * Perf rewrite (scandicommerce):
 * - carousel.groupCells is a geometry-reading getter on the theme's
 *   scroll-carousel element. The old version called it inside sync() on every
 *   carousel:change/settle event (per scroll frame), interleaved with
 *   aria-current writes -> forced synchronous layout on every frame
 *   (~300ms per Lighthouse trace).
 * - Now: groupCells is read ONCE at rebuild and refreshed by the existing
 *   ResizeObserver (which fires post-layout, where reads are free). sync()
 *   uses the cached value, is coalesced to one run per animation frame, and
 *   only writes aria-current when the active page actually changed.
 */
(function () {
  'use strict';

  if (window.__scrollCarouselPageDotsLoaded) return;
  window.__scrollCarouselPageDotsLoaded = true;

  function bindDots(el) {
    const carouselId = el.getAttribute('for');
    if (!carouselId) return;

    const bind = () => {
      const carousel = document.getElementById(carouselId);
      if (!carousel || typeof carousel.select !== 'function') return;

      const signal = el._abort.signal;
      let buttons = [];

      // Cached geometry — refreshed only at rebuild and on resize.
      let perPage = 1;
      let currentPage = -1;

      const readPerPage = () => Math.max(1, carousel.groupCells || 1);
      const pageCount = () => Math.max(1, Math.ceil(carousel.cells.length / perPage));

      const sync = (idx) => {
        if (buttons.length === 0) return;
        const page = Math.min(buttons.length - 1, Math.floor(idx / perPage));
        if (page === currentPage) return; // no write, no style invalidation
        currentPage = page;
        buttons.forEach((btn, i) => btn.setAttribute('aria-current', i === page ? 'true' : 'false'));
      };

      // Coalesce change/settle bursts to at most one sync per frame.
      let rafId = null;
      let pendingIdx = 0;
      const scheduleSync = (idx) => {
        pendingIdx = idx;
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          sync(pendingIdx);
        });
      };

      const rebuild = () => {
        perPage = readPerPage(); // single geometry read per rebuild
        currentPage = -1;

        const count = pageCount();
        el.replaceChildren();
        if (count <= 1) {
          el.hidden = true;
          buttons = [];
          return;
        }

        el.hidden = false;
        const frag = document.createDocumentFragment();
        for (let i = 0; i < count; i += 1) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tap-area';
          btn.setAttribute('aria-current', 'false');
          const label = document.createElement('span');
          label.className = 'sr-only';
          label.textContent = `Page ${i + 1}`;
          btn.appendChild(label);
          frag.appendChild(btn);
        }
        el.appendChild(frag);
        buttons = Array.from(el.querySelectorAll('button'));
        buttons.forEach((btn, i) => {
          btn.addEventListener(
            'click',
            () => {
              carousel.select(Math.min(i * perPage, carousel.cells.length - 1));
            },
            { signal }
          );
        });
        sync(typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : 0);
      };

      const onChange = (e) => scheduleSync(e.detail.index);
      carousel.addEventListener('carousel:change', onChange, { signal });
      carousel.addEventListener('carousel:settle', onChange, { signal });

      rebuild();

      // ResizeObserver callbacks run after layout, so geometry reads here are
      // free — this is the one place perPage gets refreshed.
      const ro = new ResizeObserver(() => {
        const nextPerPage = readPerPage();
        if (nextPerPage !== perPage) {
          rebuild();
        } else {
          scheduleSync(typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : 0);
        }
      });
      ro.observe(carousel);
      el._resizeObserver = ro;
    };

    customElements.whenDefined('scroll-carousel').then(() => requestAnimationFrame(bind));
  }

  function init(root) {
    (root || document).querySelectorAll('scroll-carousel-page-dots:not([data-bound])').forEach((el) => {
      el.dataset.bound = '1';
      el._abort = new AbortController();
      bindDots(el);
    });
  }

  try {
    customElements.define(
      'scroll-carousel-page-dots',
      class extends HTMLElement {
        connectedCallback() {
          if (!this.hasAttribute('for')) return;
          init(this.closest('.shopify-section') || document);
        }
        disconnectedCallback() {
          this._abort?.abort();
          this._resizeObserver?.disconnect();
        }
      }
    );
  } catch (e) {
    /* already defined */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();